import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import Link from "next/link";
import StarRating from "../components/StarRating";
import { walkMinutesFrom } from "../utils";

const styles = `
.tr-csec__title {
  font-size: clamp(24px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  text-align: center;
  margin: 56px 0 16px;
  padding: 0 16px;
}
.tr-csec__intro {
  max-width: 760px;
  margin: 0 auto 36px;
  padding: 0 16px;
  font-size: 15px;
  line-height: 1.7;
  color: #374151;
  text-align: center;
}
.tr-csec__intro p { margin: 0 0 10px; }
.tr-csec__intro a { color: var(--primary, #1b579e); text-decoration: underline; }

/* ── Filtres catégories ── */
.tr-csec__cats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 48px;
  padding: 0 16px;
}
.tr-csec__cat {
  padding: 10px 22px;
  border-radius: 999px;
  border: none;
  background: color-mix(in srgb, var(--primary, #1b579e) 12%, white);
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .15s, color .15s, transform .15s;
}
.tr-csec__cat:hover { background: color-mix(in srgb, var(--primary, #1b579e) 20%, white); }
.tr-csec__cat--active { background: var(--primary, #1b579e); color: white; }

/* ── Section par catégorie ── */
.tr-csec__section { margin-bottom: 56px; }
.tr-csec__section-title {
  font-size: clamp(20px, 2.5vw, 28px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 0 0 28px;
}

/* ── Grille ── */
.tr-csec__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 360px));
  gap: 24px;
  justify-content: center;
  max-width: 1180px;
  margin: 0 auto;
}
@media (max-width: 640px) {
  .tr-csec__grid { grid-template-columns: minmax(0, 400px); }
}

/* ── Carte ── */
.tr-ccard {
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
  transition: transform .25s ease, box-shadow .25s ease;
  background: #1a1a2e;
  text-decoration: none;
  color: inherit;
  display: block;
}
.tr-ccard:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(0,0,0,.16), 0 2px 6px rgba(0,0,0,.06);
}
.tr-ccard__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-ccard:hover .tr-ccard__img { transform: scale(1.06); }
.tr-ccard__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: #4b5563;
  font-size: 40px;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
}
.tr-ccard__wash {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(9,18,26,0) 30%, rgba(9,18,26,.55) 60%, rgba(9,18,26,.92) 100%);
}
.tr-ccard__body {
  position: absolute; inset: auto 0 0 0;
  padding: 18px 20px 20px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tr-ccard__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.tr-ccard__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: .02em;
  background: var(--primary, #1b579e);
  color: white;
}
.tr-ccard__badge--tag { background: #f59e0b; color: #1a1a2e; }
.tr-ccard__name {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: white;
}
.tr-ccard__subtitle {
  font-size: 13px;
  color: rgba(255,255,255,.85);
  line-height: 1.3;
  margin-top: -4px;
}
.tr-ccard__meta {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  color: rgba(255,255,255,.88);
  line-height: 1.35;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,.15);
  flex-wrap: wrap;
}
.tr-ccard__meta-item { display: flex; align-items: center; gap: 6px; }
.tr-ccard__meta-icon { flex-shrink: 0; margin-top: 1px; }

/* ── Empty / introuvable ── */
.tr-csec__empty {
  text-align: center;
  padding: 64px 16px;
  color: #6b7280;
  font-size: 15px;
}
`;

function ItemCard({ context, item, category, section, lang }) {
    const localized = applyLang(item, lang);
    const localizedCat = category ? applyLang(category, lang) : null;
    const imgSrc = item.image ? `${ASSET_URL}${item.image}` : null;
    const address = item.address?.address;
    const walkMinutes = walkMinutesFrom(context.org, item);
    const fields = section?.fields || {};
    const showNote = !!fields.note;
    const badge = fields.badge ? localized.badge : null;
    const subtitle = fields.subtitle ? localized.subtitle : null;
    const placeholderIcon = section?.icon?.name ? section.icon : { name: "IconMapPin", source: "tabler" };

    return (
        <Link href={context.org.BASE_WEB_URL + "/travel/c/" + section.slug + "/" + item.id} legacyBehavior>
            <a className="tr-ccard">
                {imgSrc ? (
                    <img src={imgSrc} alt={localized.name} className="tr-ccard__img" loading="lazy" />
                ) : (
                    <div className="tr-ccard__img-placeholder">
                        {renderIcon(placeholderIcon, 48)}
                    </div>
                )}
                <div className="tr-ccard__wash" />
                <div className="tr-ccard__body">
                    <div className="tr-ccard__badges">
                        {localizedCat?.label && (
                            <span className="tr-ccard__badge">{localizedCat.label}</span>
                        )}
                        {badge && (
                            <span className="tr-ccard__badge tr-ccard__badge--tag">{badge}</span>
                        )}
                        {showNote && <StarRating value={item.note} size={15} />}
                    </div>
                    <div className="tr-ccard__name">{localized.name}</div>
                    {subtitle && <div className="tr-ccard__subtitle">{subtitle}</div>}
                    {(address || walkMinutes != null) && (
                        <div className="tr-ccard__meta">
                            {address && (
                                <span className="tr-ccard__meta-item">
                                    <span className="tr-ccard__meta-icon">
                                        {renderIcon({ name: "IconMapPin", source: "tabler" }, 14)}
                                    </span>
                                    <span>{address}</span>
                                </span>
                            )}
                            {walkMinutes != null && (
                                <span className="tr-ccard__meta-item">
                                    <span className="tr-ccard__meta-icon">
                                        {renderIcon({ name: "IconWalk", source: "tabler" }, 14)}
                                    </span>
                                    <span><Trans>{walkMinutes} min</Trans></span>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </a>
        </Link>
    );
}

export default function CustomSectionList({ context, loading }) {
    const { org, travel } = context;

    const slug = context.route?.[2];
    const rawSection = (travel?.sections || []).find(s => s.slug === slug);

    // Hooks AVANT tout return conditionnel (règle React).
    const [activeCat, setActiveCat] = React.useState(null);

    const section = rawSection ? applyLang(rawSection, context.lang) : null;
    const categories = (rawSection?.categories || []).map(c => applyLang(c, context.lang));
    const allItems = (travel?.sectionItems || [])
        .filter(i => rawSection && i.sectionId === rawSection.id)
        .slice()
        .sort((a, b) => (a.position || 0) - (b.position || 0));

    const usedCatIds = new Set(allItems.map(i => i.categoryId).filter(Boolean));
    const activeCategories = categories.filter(c => usedCatIds.has(c.id));

    const visibleItems = activeCat ? allItems.filter(i => i.categoryId === activeCat) : allItems;

    const itemsByCategory = React.useMemo(() => {
        const map = new Map();
        for (const cat of activeCategories) {
            map.set(cat.id, visibleItems.filter(i => i.categoryId === cat.id));
        }
        const orphans = visibleItems.filter(i => !i.categoryId || !activeCategories.find(c => c.id === i.categoryId));
        if (orphans.length > 0) map.set("__other__", orphans);
        return map;
    }, [visibleItems, activeCategories]);

    const categoryById = React.useMemo(() => {
        const map = new Map();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    // Rubrique introuvable (slug inconnu).
    if (!rawSection) {
        return (
            <div className="travel-page travel-page--custom-section">
                <style dangerouslySetInnerHTML={{ __html: styles }} />
                <Meta context={context} title={org.name} description={org.name} />
                {loading && <Loader />}
                <Header context={context} />
                <main className="travel-main">
                    <div className="tr-csec__empty">
                        <h1><Trans>Rubrique introuvable</Trans></h1>
                        <Link href={org.BASE_WEB_URL} legacyBehavior>
                            <a style={{ color: "var(--primary, #1b579e)" }}><Trans>Retour à l'accueil</Trans></a>
                        </Link>
                    </div>
                </main>
                <Footer context={context} />
            </div>
        );
    }

    const intro = section.description;

    const jsonLd = allItems.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: section.label,
        itemListElement: allItems.slice(0, 50).map((it, i) => {
            const loc = applyLang(it, context.lang);
            return {
                "@type": "ListItem",
                position: i + 1,
                item: {
                    "@type": "Place",
                    name: loc.name,
                    image: it.image ? (it.image.startsWith("http") ? it.image : `${ASSET_URL}${it.image}`) : undefined,
                    address: it.address?.address ? { "@type": "PostalAddress", streetAddress: it.address.address } : undefined,
                    geo: (it.address?.lat && it.address?.lon) ? {
                        "@type": "GeoCoordinates", latitude: it.address.lat, longitude: it.address.lon,
                    } : undefined,
                },
            };
        }),
    } : null;

    return (
        <div className="travel-page travel-page--custom-section tr-csec">
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <Meta
                context={context}
                title={section.label}
                description={(intro || "").replace(/<[^>]*>/g, "").slice(0, 200) || section.label}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-csec__title">{section.label}</h1>

                    {intro && (
                        <div className="tr-csec__intro" dangerouslySetInnerHTML={{ __html: intro }} />
                    )}

                    {activeCategories.length > 0 && (
                        <div className="tr-csec__cats">
                            {activeCategories.map(cat => (
                                <button
                                    key={cat.id}
                                    className={`tr-csec__cat${activeCat === cat.id ? " tr-csec__cat--active" : ""}`}
                                    onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {allItems.length === 0 && (
                        <div className="tr-csec__empty">
                            <Trans>Aucun élément à afficher pour le moment.</Trans>
                        </div>
                    )}

                    {Array.from(itemsByCategory.entries()).map(([catId, items]) => {
                        if (items.length === 0) return null;
                        const cat = catId === "__other__" ? null : categoryById.get(catId);
                        const catLocalized = cat ? applyLang(cat, context.lang) : null;
                        return (
                            <section key={catId} className="tr-csec__section">
                                {activeCategories.length > 0 && (
                                    <h2 className="tr-csec__section-title">
                                        {catLocalized?.label || <Trans>Autres</Trans>}
                                    </h2>
                                )}
                                <div className="tr-csec__grid">
                                    {items.map(item => (
                                        <ItemCard
                                            context={context}
                                            key={item.id}
                                            item={item}
                                            category={cat}
                                            section={rawSection}
                                            lang={context.lang}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
